import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db,auth } from "../../firebase/firebaseConfig";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import Button from "../../components/Button";

import imgResultadoCriteriosGLIM from "../../images/criterios-glim-resultado.png";

// ===== Utilidades
const computeIMC = (peso, tallaCm) => {
  const w = Number(peso);
  const h = Number(tallaCm) / 100;
  if (!w || !h) return null;
  const imc = w / (h * h);
  return isFinite(imc) ? Number(imc.toFixed(2)) : null;
};

// ===== EAT-10 (nuevo orden y texto)
const EAT10_ITEMS = [
  "Mi problema para tragar me ha llevado a perder peso",
  "Mi problema para tragar interfiere con mi capacidad para comer fuera de casa",
  "Tragar líquidos me supone un esfuerzo extra",
  "Tragar sólidos me supone un esfuerzo extra",
  "Tragar pastillas me supone un esfuerzo extra",
  "Tragar es doloroso",
  "El placer de comer se ve afectado por mi problema para tragar",
  "Cuando trago, la comida se pega en mi garganta",
  "Toso cuando como",
  "Tragar es estresante",
];

// ===== Tipos y etiquetas (GLIM solo como CRITERIOS-GLIM)
const FORM_TYPES = {
  "EAT-10": "EAT10",
  "CRIBADO-NUTRICIONAL": "NUTRISK",
  "CRITERIOS-GLIM": "CRITERIOS-GLIM",
  "CAVIDAD-ORAL": "CAVIDAD-ORAL",
  "MECV-V": "MECV-V",
};

const LABELS = {
  "EAT-10": "Cribado disfagia (EAT-10)",
  "CRIBADO-NUTRICIONAL": "Cribado nutricional",
  "CRITERIOS-GLIM": "Valoración nutricional",
  "CAVIDAD-ORAL": "Valoración cavidad oral",
  "MECV-V": "MECV-V",
};

// Normaliza el instrumento que llega por la URL (alias -> CRITERIOS-GLIM)
const normalizeInstrumentoId = (s) => {
  if (!s) return "";
  let t = decodeURIComponent(String(s)).trim().toLowerCase();
  t = t.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  t = t.replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();

  const mapBySlug = {
    "glim": "CRITERIOS-GLIM",
    "criterios glim": "CRITERIOS-GLIM",
    "valoracion nutricional criterios glim": "CRITERIOS-GLIM",
    "criterios glim criterios": "CRITERIOS-GLIM",
  };

  return mapBySlug[t] || s;
};

export default function CuestionarioRunner() {
  const { id: pacienteId, vid, instrumentoId } = useParams();
  const navigate = useNavigate();
  const [user] = useState(() => auth.currentUser);

  const [items, setItems] = useState({});
  const [puntuacion, setPuntuacion] = useState(0);
  const [completado, setCompletado] = useState(false);
  const [resultado, setResultado] = useState("");
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Datos de visita (para MUST - IMC actual)
  const [imcVisita, setImcVisita] = useState(null);

  // BASE (visita a base guardada en PacienteDetalle)
  const [pesoBase, setPesoBase] = useState(null);
  const [tallaBase, setTallaBase] = useState(null);
  const [imcBase, setImcBase] = useState(null);

  const [glimImgAbierta, setGlimImgAbierta] = useState(false);

  // Normalización del ID (GLIM -> CRITERIOS-GLIM)
  const normId = useMemo(() => normalizeInstrumentoId(instrumentoId), [instrumentoId]);

  const tipo = FORM_TYPES[normId] || "BASIC";
  const titulo = LABELS[normId] || normId;

  const respRef = useMemo(
    () =>
      doc(
        db,
        "pacientes",
        pacienteId,
        "visitas",
        vid,
        "respuestas",
        normId
      ),
    [pacienteId, vid, normId]
  );

  // Cargar si ya existe respuesta + BACKFILL si falta creadoPor
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(respRef);
        if (snap.exists()) {
          const data = snap.data() || {};

          // 🔁 BACKFILL: si falta creadoPor, lo fijamos al usuario actual (si hay sesión)
          if (!data.creadoPor && user) {
            try {
              await updateDoc(respRef, {
                creadoPor: user.uid,
                creadoPorEmail: user.email || null,
              });
              data.creadoPor = user.uid;
              data.creadoPorEmail = user.email || null;
            } catch (e) {
              // Si falla por reglas, seguimos sin bloquear la lectura
              console.warn("Backfill creadoPor (respuesta) falló:", e.code || e.message);
            }
          }

          setItems(data.items || {});
          setPuntuacion(Number(data.puntuacionTotal || 0));
          setCompletado(Boolean(data.completado));
          setResultado(data.resultado || "");
        } else {
          setItems({});
          setPuntuacion(0);
          setCompletado(false);
          setResultado("");
        }
      } finally {
        setCargando(false);
      }
    })();
  }, [respRef, user]);

  // Cargar **IMC de la visita** (actual)
  useEffect(() => {
    (async () => {
      try {
        const visitaRef = doc(db, "pacientes", pacienteId, "visitas", vid);
        const visitaSnap = await getDoc(visitaRef);
        if (visitaSnap.exists()) {
          const v = visitaSnap.data() || {};
          const vPeso = v.peso ?? v.medidas?.peso ?? null;
          const vTalla = v.talla ?? v.medidas?.talla ?? null;

          let imcCalc = null;
          if (v.imc != null) imcCalc = Number(v.imc);
          else if (v.medidas?.imc != null) imcCalc = Number(v.medidas.imc);
          else if (vPeso != null && vTalla != null) imcCalc = computeIMC(vPeso, vTalla);

          setImcVisita(imcCalc);
        } else {
          setImcVisita(null);
        }
      } catch {
        setImcVisita(null);
      }
    })();
  }, [ pacienteId, vid]);

  // Cargar **BASE** desde el documento del paciente (peso/talla/imc)
  useEffect(() => {
    (async () => {
      try {
        const pacienteRef = doc(db, "pacientes", pacienteId);
        const pacienteSnap = await getDoc(pacienteRef);
        if (pacienteSnap.exists()) {
          const p = pacienteSnap.data() || {};
          const pPeso = p.peso ?? null;
          const pTalla = p.talla ?? null;
          const pImc = p.imc ?? (pPeso != null && pTalla != null ? computeIMC(pPeso, pTalla) : null);

          setPesoBase(pPeso != null ? Number(pPeso) : null);
          setTallaBase(pTalla != null ? Number(pTalla) : null);
          setImcBase(pImc != null ? Number(pImc) : null);
        } else {
          setPesoBase(null);
          setTallaBase(null);
          setImcBase(null);
        }
      } catch {
        setPesoBase(null);
        setTallaBase(null);
        setImcBase(null);
      }
    })();
  }, [ pacienteId]);

  // ====== EAT-10: cálculo
  useEffect(() => {
    if (tipo !== "EAT10") return;
    const total = EAT10_ITEMS.reduce((acc, _, i) => {
      const v = Number(items[i] ?? NaN);
      return acc + (isFinite(v) ? v : 0);
    }, 0);
    setPuntuacion(total);

    const todas = EAT10_ITEMS.every((_, i) => {
      const val = items[i];
      return val !== "" && val !== undefined && val !== null;
    });
    if (todas && resultado && !completado) {
      setCompletado(true);
    }
  }, [items, tipo, resultado, completado]);

  // ====== CRIBADO NUTRICIONAL: cálculos por subform
  const seleccion = items.__tipoPaciente || ""; // "MNA" | "MST" | "NRS" | "MUST"

  const calcularMNA = () => {
    const m = items.mna || {};
    const vals = [
      "A","B","C","D","E","F","G","H","I","J","K",
      "L","M","N","O","P","Q","R"
    ].map(k => Number(m[k] ?? 0));
    const total = vals.reduce((a,b)=> a + (isFinite(b)? b : 0), 0);
    return Number(total.toFixed(1));
  };

  const calcularMST = () => {
    const mst = items.mst || {};
    const q1 = Number(mst.q1 ?? 0);
    const q1b = Number(mst.q1b ?? 0);
    const q2 = Number(mst.q2 ?? 0);
    const total = [q1, q1b, q2].reduce((a,b)=> a + (isFinite(b)? b : 0), 0);
    return total;
  };

  const calcularNRS = () => {
    const nrs = items.nrs || {};
    const qs = ["n1","n2","n3","n4"].map(k => Number(nrs[k] ?? 0));
    const total = qs.reduce((a,b)=> a + (isFinite(b)? b : 0), 0);
    return total;
  };

  const calcularMUST = () => {
    const must = items.must || {};
    const s1 = Number(must.step1 ?? 0);
    const s2 = Number(must.step2 ?? 0);
    const s3 = Number(must.step3 ?? 0);
    const total = [s1,s2,s3].reduce((a,b)=> a + (isFinite(b)? b : 0), 0);
    return total;
  };

  useEffect(() => {
    if (tipo !== "NUTRISK") return;
    let total = 0;
    if (seleccion === "MNA") total = calcularMNA();
    if (seleccion === "MST") total = calcularMST();
    if (seleccion === "NRS") total = calcularNRS();
    if (seleccion === "MUST") total = calcularMUST();
    setPuntuacion(total);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, items, seleccion]);

  const onChangeEAT10 = (index, value) => {
    setItems((prev) => ({ ...prev, [index]: value === "" ? "" : String(value) }));
  };

  const onChangeBasico = (field, value) => {
    if (field === "notas") {
      setItems((prev) => ({ ...prev, notas: value }));
    } else if (field === "puntuacionTotal") {
      setPuntuacion(value === "" ? 0 : Number(value));
    }
  };

  // Handlers NUTRISK
  const setSeleccion = (val) => {
    const map = {
      "Paciente mayor de 65 años": "MNA",
      "Paciente con cáncer": "MST",
      "Paciente hospitalizado": "NRS",
      "Paciente ambulatorio": "MUST",
    };
    setItems(prev => ({ ...prev, __selector: val, __tipoPaciente: map[val] || "" }));
    setResultado("");
  };

  const setMNA = (clave, val) => {
    setItems(prev => ({ ...prev, mna: { ...(prev.mna||{}), [clave]: val } }));
  };
  const setMST = (clave, val) => {
    setItems(prev => ({ ...prev, mst: { ...(prev.mst||{}), [clave]: val } }));
  };
  const setNRS = (clave, val) => {
    setItems(prev => ({ ...prev, nrs: { ...(prev.nrs||{}), [clave]: val } }));
  };
  const setMUST = (clave, val) => {
    setItems(prev => ({ ...prev, must: { ...(prev.must||{}), [clave]: val } }));
  };

  // Handler para CRITERIOS-GLIM
  const setGLIM = (clave, val) => {
    setItems(prev => ({ ...prev, glim: { ...(prev.glim || {}), [clave]: val } }));
  };

const setMecvvField = (field, value) =>
  setItems((prev) => ({
    ...prev,
    mecvv: {
      ...(prev.mecvv || {}),
      [field]: value,
    },
  }));

  // Guardar (con backfill si la respuesta aún no tenía creadoPor)
  const guardar = async () => {
    setGuardando(true);
    try {
      const basePayload = {
        instrumentoId: normId,
        version: 1,
        items,
        completado,
        resultado: resultado || null,
        puntuacionTotal: isFinite(Number(puntuacion)) ? Number(puntuacion) : null,
        submittedAt: serverTimestamp(),
      };

      const snap = await getDoc(respRef);

      if (snap.exists()) {
        const existing = snap.data() || {};
        // 🔁 BACKFILL solo si aún NO existe creadoPor
        if (!existing.creadoPor && user) {
          basePayload.creadoPor = user.uid;
          basePayload.creadoPorEmail = user.email || null;
        }
        await updateDoc(respRef, basePayload);
      } else {
        await setDoc(respRef, {
          ...basePayload,
          // en creación nueva sí ponemos autor
          creadoPor: user?.uid || "",
          creadoPorEmail: user?.email || "",
          createdAt: serverTimestamp(),
        });
      }

      alert("Cuestionario guardado ✅");
      navigate(`/pacientes/${pacienteId}/visitas/${vid}`);
    } catch (e) {
      console.error("save cuestionario error:", e);
      alert("No se pudo guardar. Revisa la consola.");
    } finally {
      setGuardando(false);
    }
  };

  // Crear (si hace falta) y abrir "Valoración cavidad oral"
  const abrirCavidadOral = async () => {
    const ref = doc(db, "pacientes", pacienteId, "visitas", vid, "respuestas", "CAVIDAD-ORAL");
    const s = await getDoc(ref);
    if (!s.exists()) {
      await setDoc(ref, {
        instrumentoId: "CAVIDAD-ORAL",
        version: 1,
        items: {},
        completado: false,
        creadoPor: user?.uid || "",
        creadoPorEmail: user?.email || "",
        createdAt: serverTimestamp(),
      });
    }
    navigate(`/pacientes/${pacienteId}/visitas/${vid}/cuestionarios/CAVIDAD-ORAL`);
  };

  // Abrir CRITERIOS-GLIM (desde otros cribados)
  const abrirGLIM = async () => {
    const ref = doc(db, "pacientes", pacienteId, "visitas", vid, "respuestas", "CRITERIOS-GLIM");
    const s = await getDoc(ref);
    if (!s.exists()) {
      await setDoc(ref, {
        instrumentoId: "CRITERIOS-GLIM",
        version: 1,
        items: {},
        completado: false,
        creadoPor: user?.uid || "",
        creadoPorEmail: user?.email || "",
        createdAt: serverTimestamp(),
      });
    }
    navigate(`/pacientes/${pacienteId}/visitas/${vid}/cuestionarios/CRITERIOS-GLIM`);
  };

  if (cargando) {
    return (
      <div className="p-4 max-w-xl mx-auto">
        <p>Cargando cuestionario…</p>
      </div>
    );
  }

  // ======= UI helpers
  const Box = ({ children }) => (
    <div className="bg-white border rounded-lg p-4 leading-relaxed">{children}</div>
  );

  const RecoBox = ({ title="RECOMENDACIONES", children }) => (
    <div className="bg-white border-2 border-dashed rounded-lg p-4">
      <h5 className="font-bold mb-2">{title}</h5>
      <div className="whitespace-pre-line">{children}</div>
    </div>
  );

  return (
    <div className="p-4 max-w-xl mx-auto flex flex-col gap-4">
{/* Encabezado: botón Volver a la visita + título del cuestionario a la derecha */}
<div className="flex items-center justify-between">
  <Button
    onClick={() => navigate(`/pacientes/${pacienteId}/visitas/${vid}`)}
    className="flex items-center gap-1 px-3 py-1.5"
  >
    <span>Volver a la visita</span>
  </Button>

  <h4 className="font-bold text-right">{titulo}</h4>
</div>

      {/* ====== EAT-10 ====== */}
      {tipo === "EAT10" ? (
        <>
          <Box>
            <p className="mb-2">
              Debe realizar el cribado de disfagia para conocer el riesgo, o no, de deterioro de la deglución.
            </p>
            <p className="mb-2">
              El test EAT-10 permite conocer la dificultad para tragar.
            </p>
            <p>Rellenar según la escala 0 (ningún problema) a 4 (problema serio).</p>
          </Box>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              guardar();
            }}
            className="bg-[#dfeaf7] p-4 rounded-lg shadow flex flex-col gap-4"
          >
            {EAT10_ITEMS.map((texto, i) => (
              <div key={i} className="flex flex-col gap-1">
                <label className="font-semibold text-justify">{`${i + 1}. ${texto}`}</label>
                <select
                  className="border rounded p-2 bg-white"
                  value={items[i] ?? ""}
                  onChange={(e) => onChangeEAT10(i, e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="0">0 - Ningún problema</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4 - Problema serio</option>
                </select>
              </div>
            ))}

            <div className="flex flex-col gap-3">
              <div className="text-right font-semibold">Puntuación total: {puntuacion}</div>

              <div className="text-justify">Si la puntuación total que obtuvo es mayor o igual a 3, el paciente puede presentar problemas para tragar de manera segura y eficaz.</div>

              <div>
                <label className="font-semibold text-left block mb-1">Resultado</label>
                <select
                  className="border rounded p-2 bg-white w-full"
                  value={resultado}
                  onChange={(e) => setResultado(e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="No presenta riesgo de disfagia">No presenta riesgo de disfagia</option>
                  <option value="Presenta riesgo de disfagia">Presenta riesgo de disfagia</option>
                </select>
              </div>

              {resultado === "Presenta riesgo de disfagia" && (
                <div className="bg-white border rounded p-3 text-center flex flex-col gap-2">
                  <p>
                    Se recomienda cumplimentar el cuestionario de <strong>“Valoración cavidad oral”</strong>.
                  </p>
                  <div>
                    <Button onClick={abrirCavidadOral}>Abrir “Valoración cavidad oral”</Button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  id="chkComp"
                  type="checkbox"
                  className="w-4 h-4"
                  checked={completado}
                  onChange={(e) => setCompletado(e.target.checked)}
                />
                <label htmlFor="chkComp" className="mt-4 mb-4">
                  Marcar como completado
                </label>
              </div>
            </div>

            <div className="flex justify-center">
              <Button
                type="submit"
                className="mx-auto bg-degluapp-green hover:bg-degluapp-green-hover text-white"
                disabled={guardando}
              >
                {guardando ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </form>
        </>
      ) : null}

      {/* ====== CRIBADO NUTRICIONAL ====== */}
      {tipo === "NUTRISK" ? (
        <>
          <Box>
            <p>Debe seleccionar el cribado óptimo para su paciente, según su situación y/o ámbito sanitario:</p>
          </Box>

          {/* Selector principal */}
          <div className="bg-white border rounded p-3">
            <label className="font-semibold block mb-1">Seleccione el tipo de paciente</label>
            <select
              className="border rounded p-2 w-full"
              value={items.__selector || ""}
              onChange={(e) => setSeleccion(e.target.value)}
            >
              <option value="">Seleccione…</option>
              <option>Paciente mayor de 65 años</option>
              <option>Paciente con cáncer</option>
              <option>Paciente hospitalizado</option>
              <option>Paciente ambulatorio</option>
            </select>
          </div>

          {/* ===== MNA ===== */}
          {items.__tipoPaciente === "MNA" && (
            <div className="bg-[#dfeaf7] p-4 rounded-lg shadow flex flex-col gap-4">
              <h5 className="font-bold text-center">Mini Nutritional Assessment (MNA)</h5>
              <Box>
                <p className="italic">RECUERDE: este cuestionario es específico para personas mayores de 65 años.</p>
              </Box>

              {[
                { k:"A", t:"A. ¿Ha perdido el apetito?... últimos 3 meses?", opts:[["0","0 - ha comido mucho menos"],["1","1 - ha comido menos"],["2","2 - ha comido igual"]] },
                { k:"B", t:"B. Pérdida reciente de peso (< 3 meses)", opts:[["0","0 - > 3 kg"],["1","1 - no lo sabe"],["2","2 - pérdida entre 1 a 3 kg"],["3","3 - no ha habido peso"]] },
                { k:"C", t:"C. Movilidad", opts:[["0","0 - de la cama al sillón"],["1","1 - autonomía en el interior"],["2","2 - sale del domicilio"]] },
                { k:"D", t:"D. ¿Enfermedad aguda o estrés psicológico en los últimos 3 meses?", opts:[["0","0 - no"],["1","1 - sí"]] },
                { k:"E", t:"E. Problemas neuropsicológicos", opts:[["0","0 - demencia o depresión grave"],["1","1 - demencia leve"],["2","2 - sin problemas psicológicos"]] },
                { k:"F", t:"F. Índice de masa corporal (IMC)", opts:[["0","0 - IMC < 19"],["1","1 - 19 ≤ IMC < 21"],["2","2 - 21 ≤ IMC < 23"],["3","3 - IMC ≥ 23"]] },
                { k:"G", t:"G. ¿El paciente vive independiente en su domicilio?", opts:[["0","0 - no"],["1","1 - sí"]] },
                { k:"H", t:"H. ¿Toma más de 3 medicamentos al día?", opts:[["0","0 - no"],["1","1 - sí"]] },
                { k:"I", t:"I. ¿Úlceras o lesiones cutáneas?", opts:[["0","0 - no"],["1","1 - sí"]] },
                { k:"J", t:"J. ¿Cuántas comidas completas toma al día?", opts:[["0","0 - 1 comida"],["1","1 - 2 comidas"],["2","2 - 3 comidas"]] },
              ].map((q)=>(
                <div key={q.k} className="flex flex-col gap-1">
                  <label className="font-semibold">{q.t}</label>
                  <select
                    className="border rounded p-2 bg-white"
                    value={(items.mna?.[q.k] ?? "")}
                    onChange={(e)=> setMNA(q.k, e.target.value)}
                  >
                    <option value="">Seleccione…</option>
                    {q.opts.map(([v,lab])=> <option key={v} value={v}>{lab}</option>)}
                  </select>
                </div>
              ))}

              {/* K con 3 subpreguntas y puntuación 0 | 0.5 | 1 */}
              <div className="flex flex-col gap-2">
                <label className="font-semibold">K. ¿Consume el paciente…?</label>
                {[
                  "…productos lácteos al menos una vez al día?",
                  "…huevos o legumbres 1 o 2 veces a la semana?",
                  "…carne, pescado o aves, diariamente?",
                ].map((tx,i)=>(
                  <div key={i} className="flex items-center gap-2">
                    <span className="flex-1">{tx}</span>
                    <select
                      className="border rounded p-2 bg-white"
                      value={items.mna?.[`K${i+1}`] ?? ""}
                      onChange={(e)=> setMNA(`K${i+1}`, e.target.value)}
                    >
                      <option value="">Seleccione…</option>
                      <option value="1">Sí</option>
                      <option value="0">No</option>
                    </select>
                  </div>
                ))}
                <div className="text-right">
                  {(() => {
                    const s = ["K1","K2","K3"].reduce((a,k)=> a + (Number(items.mna?.[k] ?? 0)), 0);
                    const kScore = s >= 3 ? 1 : (s === 2 ? 0.5 : 0);
                    if ((items.mna?.K ?? null) !== kScore) {
                      setTimeout(()=> setMNA("K", String(kScore)), 0);
                    }
                    return <>Puntuación K: <strong>{kScore}</strong></>;
                  })()}
                </div>
              </div>

              {[
                { k:"L", t:"L. ¿Consume frutas o verduras al menos 2 veces al día?", opts:[["0","0 - no"],["1","1 - sí"]] },
                { k:"M", t:"M. ¿Cuántos vasos de agua u otros líquidos toma al día?", opts:[["0","0 - menos de 3 vasos"],["0.5","0.5 - de 3 a 5 vasos"],["1","1 - más de 5 vasos"]] },
                { k:"N", t:"N. Forma de alimentarse", opts:[["0","0 - necesita ayuda"],["1","1 - se alimenta solo con dificultad"],["2","2 - se alimenta solo sin dificultad"]] },
                { k:"O", t:"O. ¿Se considera el paciente que está bien nutrido?", opts:[["0","0 - malnutrición grave"],["1","1 - no lo sabe o malnutrición moderada"],["2","2 - sin problemas de nutrición"]] },
                { k:"P", t:"P. En comparación con las personas de su edad, ¿cómo encuentra su estado de salud?", opts:[["0","0 - peor"],["0.5","0.5 - no lo sabe"],["1","1 - igual"],["2","2 - mejor"]] },
                { k:"Q", t:"Q. Circunferencia braquial (CB en cm)", opts:[["0","0 - CB < 21"],["0.5","0.5 - 21 ≤ CB ≤ 22"],["1","1 - CB > 22"]] },
                { k:"R", t:"R. Circunferencia de la pantorrilla (CP en cm)", opts:[["0","0 - CP < 31"],["1","1 - CP ≥ 31"]] },
              ].map((q)=>(
                <div key={q.k} className="flex flex-col gap-1">
                  <label className="font-semibold">{q.t}</label>
                  <select
                    className="border rounded p-2 bg-white"
                    value={(items.mna?.[q.k] ?? "")}
                    onChange={(e)=> setMNA(q.k, e.target.value)}
                  >
                    <option value="">Seleccione…</option>
                    {q.opts.map(([v,lab])=> <option key={v} value={v}>{lab}</option>)}
                  </select>
                </div>
              ))}

              <div className="text-right font-semibold">Puntuación total MNA: {calcularMNA()}</div>

              <div>
                <label className="font-semibold block mb-1">Clasificación</label>
                <select
                  className="border rounded p-2 bg-white w-full"
                  value={resultado}
                  onChange={(e)=> setResultado(e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="Estado nutricional normal > 24">Estado nutricional normal &gt; 24</option>
                  <option value="Riesgo de malnutrición 17-24">Riesgo de malnutrición 17-24</option>
                  <option value="Malnutrición < 17">Malnutrición &lt; 17</option>
                </select>
              </div>

              {resultado === "Estado nutricional normal > 24" && (
                <RecoBox>
{`REEVALUACIÓN:
Después de un acontecimiento o enfermedad aguda.
Una vez al año en los ancianos que viven en su casa.
Cada 3 meses en ancianos que viven en residencias o están hospitalizados.`}
                </RecoBox>
              )}

              {resultado === "Riesgo de malnutrición 17-24" && (
                <RecoBox>
{`CONTROL (SI NO HAY PERDIDA DE PESO):
Control exhaustivo del peso.
Reevaluación cada 3 meses.

TRATAMIENTO (SI HAY PÉRDIDA DE PESO):
Intervención nutricional:
~ Enriquecimiento de la dieta.
~ Suplementos nutricionales orales (400 kcal/d).
~ Control exhaustivo del peso.
~ Evaluaciones nutricionales más exhaustivas.`}
                </RecoBox>
              )}

              {resultado === "Malnutrición < 17" && (
                <RecoBox>
{`TRATAMIENTO:
Intervención nutricional:
~ Enriquecimiento de la dieta.
~ Suplementos nutricionales orales (400-600 kcal/día).
~ Control exhaustivo del peso.
~ Evaluaciones nutricionales más exhaustivas.`}
                </RecoBox>
              )}
            </div>
          )}

          {/* ===== MST ===== */}
          {items.__tipoPaciente === "MST" && (
            <div className="bg-[#dfeaf7] p-4 rounded-lg shadow flex flex-col gap-4">
              <h5 className="font-bold text-center">Malnutrition Screening Tool (MST)</h5>
              <Box><p className="italic">RECUERDE: este cuestionario es específico para personas con cáncer.</p></Box>

              <div className="flex flex-col gap-1">
                <label className="font-semibold">1. ¿Ha perdido peso de manera involuntaria recientemente?</label>
                <select
                  className="border rounded p-2 bg-white"
                  value={items.mst?.q1 ?? ""}
                  onChange={(e)=> setMST("q1", e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="0">0 - no</option>
                  <option value="2">2 - no estoy seguro</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-semibold">Si ha perdido peso, ¿cuántos kilogramos?</label>
                <select
                  className="border rounded p-2 bg-white"
                  value={items.mst?.q1b ?? ""}
                  onChange={(e)=> setMST("q1b", e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="1">1 - de 1 a 5 kg</option>
                  <option value="2">2 - de 6 a 10 kg</option>
                  <option value="3">3 - de 11 a 15 kg</option>
                  <option value="4">4 - &gt; 15 kg</option>
                  <option value="0">0 - no estoy seguro</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-semibold">2. ¿Ha comido menos de lo normal a causa de la disminución del apetito?</label>
                <select
                  className="border rounded p-2 bg-white"
                  value={items.mst?.q2 ?? ""}
                  onChange={(e)=> setMST("q2", e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="0">0 - no</option>
                  <option value="1">1 - sí</option>
                </select>
              </div>

              <div className="text-right font-semibold">Puntuación total MST: {calcularMST()}</div>

              <div>
                <label className="font-semibold block mb-1">Clasificación</label>
                <select
                  className="border rounded p-2 bg-white w-full"
                  value={resultado}
                  onChange={(e)=> setResultado(e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="Sin riesgo">Sin riesgo</option>
                  <option value="Riesgo de malnutrición">Riesgo de malnutrición</option>
                </select>
              </div>

              {resultado === "Sin riesgo" && (
                <Box><p>Reevaluar semanalmente.</p></Box>
              )}

              {resultado === "Riesgo de malnutrición" && (
                <div className="flex flex-col gap-2">
                  <Box><p>Realizar valoración nutricional completa.</p></Box>
                  <div className="text-center">
                    <Button onClick={abrirGLIM}>Abrir “Valoración nutricional”</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== NRS ===== */}
          {items.__tipoPaciente === "NRS" && (
            <div className="bg-[#dfeaf7] p-4 rounded-lg shadow flex flex-col gap-4">
              <h5 className="font-bold text-center">Nutritional Risk Screening (NRS)</h5>
              <Box><p className="italic">RECUERDE: este cuestionario es específico para personas hospitalizadas.</p></Box>

              {[
                { k:"n1", t:"1. ¿IMC < 20.5?" },
                { k:"n2", t:"2. ¿Pérdida de peso en los últimos 3 meses?" },
                { k:"n3", t:"3. ¿Disminución de la ingesta en la última semana?" },
                { k:"n4", t:"4. ¿Enfermedad grave?" },
              ].map(q=>(
                <div key={q.k} className="flex flex-col gap-1">
                  <label className="font-semibold">{q.t}</label>
                  <select
                    className="border rounded p-2 bg-white"
                    value={items.nrs?.[q.k] ?? ""}
                    onChange={(e)=> setNRS(q.k, e.target.value)}
                  >
                    <option value="">Seleccione…</option>
                    <option value="0">0 - no</option>
                    <option value="1">1 - sí</option>
                  </select>
                </div>
              ))}

              <div className="text-right font-semibold">Puntuación total NRS: {calcularNRS()}</div>

              <div>
                <label className="font-semibold block mb-1">Clasificación</label>
                <select
                  className="border rounded p-2 bg-white w-full"
                  value={resultado}
                  onChange={(e)=> setResultado(e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="Sin riesgo">Sin riesgo</option>
                  <option value="Riesgo de malnutrición ≥ 3">Riesgo de malnutrición ≥ 3</option>
                </select>
              </div>

              {resultado === "Sin riesgo" && (
                <Box><p>Reevaluar semanalmente.</p></Box>
              )}

              {resultado === "Riesgo de malnutrición ≥ 3" && (
                <div className="flex flex-col gap-3">
                  <Box><p>Realizar valoración nutricional completa.</p></Box>
                  <div className="text-center">
                    <Button onClick={abrirGLIM}>Abrir “Valoración nutricional”</Button>
                  </div>
                  <p className="text-center">
                    Si va a ser sometido a cirugía mayor, iniciar suplementación preoperativa.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ===== MUST ===== */}
          {items.__tipoPaciente === "MUST" && (
            <div className="bg-[#dfeaf7] p-4 rounded-lg shadow flex flex-col gap-4">
              <h5 className="font-bold text-center">Malnutrition Universal Screening Tool (MUST)</h5>
              <Box><p className="italic">RECUERDE: este cuestionario es específico para pacientes ambulatorios.</p></Box>

              {/* Paso 1: IMC (visita) */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold">Paso 1. IMC = peso en kg / talla en m²</label>
                <div className="mb-1">
                  IMC de esta visita: <strong>{imcVisita ?? "—"}</strong>
                </div>
                <select
                  className="border rounded p-2 bg-white"
                  value={items.must?.step1 ?? ""}
                  onChange={(e)=> setMUST("step1", e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="0">0 - ≥ 20</option>
                  <option value="1">1 - 18.5 a 20</option>
                  <option value="2">2 - ≤ 18.5</option>
                </select>
              </div>

              {/* Paso 2: % pérdida 3-6 meses (muestra BASE) */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold">Paso 2. Pérdida de peso involuntaria en los últimos 3-6 meses</label>
                <div>
                  Peso inicial (visita base): <strong>{pesoBase ?? "—"}</strong>
                </div>
                <div className="text-gray-600">
                  Talla base: <strong>{tallaBase ?? "—"}</strong> · IMC base: <strong>{imcBase ?? "—"}</strong>
                </div>
                <select
                  className="border rounded p-2 bg-white"
                  value={items.must?.step2 ?? ""}
                  onChange={(e)=> setMUST("step2", e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="0">0 - ≤ 5 %</option>
                  <option value="1">1 - 5 a 10 %</option>
                  <option value="2">2 - ≥ 10 %</option>
                </select>
              </div>

              {/* Paso 3: enfermedad aguda sin aporte >5 días */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold">Paso 3. Paciente con enfermedad aguda y sin aporte &gt; 5 días</label>
                <select
                  className="border rounded p-2 bg-white"
                  value={items.must?.step3 ?? ""}
                  onChange={(e)=> setMUST("step3", e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="0">0 - no</option>
                  <option value="1">1 - sí</option>
                </select>
              </div>

              <div className="text-right font-semibold">Puntuación total MUST: {calcularMUST()}</div>

              <div>
                <label className="font-semibold block mb-1">Clasificación</label>
                <select
                  className="border rounded p-2 bg-white w-full"
                  value={resultado}
                  onChange={(e)=> setResultado(e.target.value)}
                >
                  <option value="">Seleccione…</option>
                  <option value="Riesgo bajo = 0">Riesgo bajo = 0</option>
                  <option value="Riesgo medio = 1">Riesgo medio = 1</option>
                  <option value="Riesgo alto = 2 o +">Riesgo alto = 2 o +</option>
                </select>
              </div>

              {resultado === "Riesgo bajo = 0" && (
                <RecoBox>
{`CUIDADOS CLÍNICOS RUTINARIOS:
Repetir screening:
~ Hospital: semanal.
~ Cuidados domiciliarios: mensual.
~ Ambulatorio: grupos especiales (por ejemplo: > 75 años).`}
                </RecoBox>
              )}

              {resultado === "Riesgo medio = 1" && (
                <RecoBox>
{`OBSERVAR:
~ Hospital y cuidados domiciliarios:
Valoración de la ingesta de sólidos y líquidos de 3 días.
Si la ingesta mejora o es adecuada, no procede actuación clínica.
Si no mejora: actuar siguiendo el protocolo local.
Repetir screening semanal en el hospital y mensual en cuidados domiciliarios.
~ Comunidad:
Repetir screening cada 2-3 meses con consejo dietético si es necesario.`}
                </RecoBox>
              )}

              {resultado === "Riesgo alto = 2 o +" && (
                <RecoBox>
{`TRATAR:
Hospital, domicilio o comunidad.
Mejore o incremente la ingesta nutricional global mediante dieta culinaria primero, enriquecida si es necesario, o con suplementos.
Remitir a la Unidad de Nutrición o seguir el protocolo local.
Monitorizar y revisar el plan de cuidados: hospital (semanal) y domicilio (mensual).`}
                </RecoBox>
              )}
            </div>
          )}

          {/* Pie: marcar completado + Guardar */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              guardar();
            }}
            className="bg-white border rounded p-3 flex flex-col gap-4"
          >
            <div className="flex items-center gap-2">
              <input
                id="chkCompNut"
                type="checkbox"
                className="w-4 h-4"
                checked={completado}
                onChange={(e) => setCompletado(e.target.checked)}
              />
              <label htmlFor="chkCompNut">Marcar como completado</label>
            </div>

            <div className="text-right font-semibold">Puntuación total: {puntuacion}</div>

            <div className="flex justify-center">
              <Button
                type="submit"
                className="mx-auto bg-degluapp-green hover:bg-degluapp-green-hover text-white"
                disabled={guardando}
              >
                {guardando ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </form>
        </>
      ) : null}

      {/* ====== CRITERIOS-GLIM ====== */}
      {tipo === "CRITERIOS-GLIM" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            guardar();
          }}
          className="bg-[#dfeaf7] p-4 rounded-lg shadow flex flex-col gap-4"
        >
          <Box>
            <p className="mb-2 text-justify">
              Para valoración nutricional, se procede a proporcionar los criterios GLIM*.
            </p>
            <p className="mb-2 text-justify text-gray-700 italic">
              *Medidas técnicas de determinación de composición corporal validadas/ mediciones
              antropométricas/ examen físico nutricional, y fuerza de agarre como una medida de apoyo adicional.
            </p>
            <p className="text-justify">
              El diagnóstico de desnutrición requiere al menos 1 criterio fenotípico y 1 criterio etiológico.
            </p>
          </Box>

          <h5 className="font-bold text-center mt-2">CRITERIOS FENOTÍPICOS</h5>

          <div className="flex flex-col gap-1">
            <label className="font-semibold">1. Pérdida de peso</label>
            <select
              className="border rounded p-2 bg-white"
              value={items.glim?.p1 ?? ""}
              onChange={(e)=> setGLIM("p1", e.target.value)}
            >
              <option value="">Seleccione…</option>
              <option value="5–10 % en los últimos 6 meses o 10–20 % en más de 6 meses">
                5–10 % en los últimos 6 meses o 10–20 % en más de 6 meses
              </option>
              <option value="> 10 % en los últimos 6 meses o > 20 % en más de 6 meses">
                &gt; 10 % en los últimos 6 meses o &gt; 20 % en más de 6 meses
              </option>
              <option value="No presenta">No presenta</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-semibold">2. Índice de Masa Corporal (IMC)</label>
            <select
              className="border rounded p-2 bg-white"
              value={items.glim?.p2 ?? ""}
              onChange={(e)=> setGLIM("p2", e.target.value)}
            >
              <option value="">Seleccione…</option>
              <option value="IMC < 20 kg/m² si edad < 70 años">
                IMC &lt; 20 kg/m² si edad &lt; 70 años
              </option>
              <option value="IMC < 22 kg/m² si edad ≥ 70 años">
                IMC &lt; 22 kg/m² si edad ≥ 70 años
              </option>
              <option value="No presenta">No presenta</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-semibold">3. Masa muscular reducida</label>
            <select
              className="border rounded p-2 bg-white"
              value={items.glim?.p3 ?? ""}
              onChange={(e)=> setGLIM("p3", e.target.value)}
            >
              <option value="">Seleccione…</option>
              <option value="Leve pérdida de masa o fuerza muscular">
                Leve pérdida de masa o fuerza muscular
              </option>
              <option value="Moderada o severa pérdida de masa muscular evidente">
                Moderada o severa pérdida de masa muscular evidente
              </option>
              <option value="No presenta">No presenta</option>
            </select>
          </div>

          <h5 className="font-bold text-center mt-2">CRITERIOS ETIOLÓGICOS</h5>

          <div className="flex flex-col gap-1">
            <label className="font-semibold">4. Ingesta o asimilación reducida</label>
            <select
              className="border rounded p-2 bg-white"
              value={items.glim?.p4 ?? ""}
              onChange={(e)=> setGLIM("p4", e.target.value)}
            >
              <option value="">Seleccione…</option>
              <option value="Ingesta ≤ 50 % de lo habitual durante ≥ 1 semana">
                Ingesta ≤ 50 % de lo habitual durante ≥ 1 semana
              </option>
              <option value="Ingesta reducida crónica (≥ 2 semanas)">
                Ingesta reducida crónica (≥ 2 semanas)
              </option>
              <option value="Problemas de absorción intestinal o digestiva">
                Problemas de absorción intestinal o digestiva
              </option>
              <option value="No presenta">No presenta</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-semibold">5. Enfermedad o inflamación</label>
            <select
              className="border rounded p-2 bg-white"
              value={items.glim?.p5 ?? ""}
              onChange={(e)=> setGLIM("p5", e.target.value)}
            >
              <option value="">Seleccione…</option>
              <option value="Enfermedad aguda o cirugía reciente">Enfermedad aguda o cirugía reciente</option>
              <option value="Enfermedad crónica o inflamatoria (EPOC, cáncer, insuficiencia, etc.)">
                Enfermedad crónica o inflamatoria (EPOC, cáncer, insuficiencia, etc.)
              </option>
              <option value="Otra enfermedad con componente inflamatorio">Otra enfermedad con componente inflamatorio</option>
              <option value="No presenta">No presenta</option>
            </select>
          </div>

          <div className="text-justify">Para poder obtener el resultado correcto con la información anteriormente cumplimentada debemos fijarnos en la siguiente imagen:</div>

          <img
            src={imgResultadoCriteriosGLIM}
            alt="Resultado Criterios GLIM"
            className="w-full max-w-md mx-auto mb-6 cursor-pointer hover:opacity-90 transition"
            onClick={() => setGlimImgAbierta(true)}
          />

{glimImgAbierta && (
  <div
    className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
    onClick={() => setGlimImgAbierta(false)}
  >
    <div
      className="relative max-w-4xl w-full p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <img
        src={imgResultadoCriteriosGLIM}
        alt="Resultado Criterios GLIM ampliado"
        className="w-full rounded-lg shadow-xl"
      />

      <button
        onClick={() => setGlimImgAbierta(false)}
        className="absolute top-2 right-2 bg-white/90 rounded-full px-3 py-1 text-sm font-semibold hover:bg-white"
      >
        ✕
      </button>
    </div>
  </div>
)}


          <div>
            <label className="font-semibold block mb-1">Resultado</label>
            <select
              className="border rounded p-2 bg-white w-full"
              value={resultado}
              onChange={(e)=> setResultado(e.target.value)}
            >
              <option value="">Seleccione…</option>
              <option value="No desnutrición">No desnutrición</option>
              <option value="Desnutrición moderada">Desnutrición moderada</option>
              <option value="Desnutrición severa">Desnutrición severa</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="chkCompGlim"
              type="checkbox"
              className="w-4 h-4"
              checked={completado}
              onChange={(e) => setCompletado(e.target.checked)}
            />
            <label htmlFor="chkCompGlim">Marcar como completado</label>
          </div>

          <div className="flex justify-center">
            <Button
              type="submit"
              className="mx-auto bg-degluapp-green hover:bg-degluapp-green-hover text-white"
              disabled={guardando}
            >
              {guardando ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </form>
      )}

      {/* ====== VALORACIÓN CAVIDAD ORAL ====== */}
      {normId === "CAVIDAD-ORAL" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            guardar();
          }}
          className="bg-[#dfeaf7] p-4 rounded-lg shadow flex flex-col gap-4"
        >
          <div className="flex flex-col gap-3">
            {/* Preguntas */}
            {[
              { k: "q1", t: "1. Piezas dentarias", o: ["Buen estado", "Regular estado", "Mal estado", "Edéntulo"] },
              { k: "q2", t: "2. Dentadura postiza", o: ["No", "Sí, bien ajustada", "Sí, mal ajustada"] },
              { k: "q3", t: "3. Mucositis", o: ["No", "Sí"] },
              { k: "q4", t: "4. Lengua y encías", o: ["Aspecto rosado y limpio", "Enrojecidas o con placa", "Con heridas o úlceras", "Con restos de comida o secreciones"] },
              { k: "q5", t: "5. Salivación", o: ["Normal", "Escasa (sequedad bucal)", "Abundante (sialorrea)"] },
              { k: "q6", t: "6. Halitosis (mal aliento)", o: ["No", "Sí"] },
              { k: "q7", t: "7. Dolor o molestias orales", o: ["No", "Sí, leves", "Sí, moderadas", "Sí, intensas"] },
            ].map((q) => (
              <div key={q.k} className="flex flex-col gap-1">
                <label className="font-semibold">{q.t}</label>
                <select
                  className="border rounded p-2 bg-white"
                  value={items.cavidadoral?.[q.k] ?? ""}
                  onChange={(e) =>
                    setItems((prev) => ({
                      ...prev,
                      cavidadoral: { ...(prev.cavidadoral || {}), [q.k]: e.target.value },
                    }))
                  }
                >
                  <option value="">Seleccione…</option>
                  {q.o.map((op) => (
                    <option key={op}>{op}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {/* Recomendaciones */}
          <div className="bg-white border-2 border-dashed rounded-lg p-4 mt-2">
            <h5 className="font-bold mb-2">RECOMENDACIONES</h5>
            <div className="whitespace-pre-line leading-relaxed">
              {`· Extremar higiene bucodental, SE ALIMENTE O NO POR VÍA ORAL.
· Realizar cepillado suave de dientes, lengua y encías, antes y después de las comidas. Puede hacer uso de un limpia lenguas.
· Retirar y cepillar las prótesis dentales después de las comidas.
· En caso de ausencias de dientes, limpiar concienzudamente lengua, paladar y encías.
· Si la persona no colabora, pasar gasa impregnada de colutorio por lengua y encías.`}
            </div>
          </div>

          {/* Completado y Guardar */}
          <div className="flex items-center gap-2 mt-3">
            <input
              id="chkCompCav"
              type="checkbox"
              className="w-4 h-4"
              checked={completado}
              onChange={(e) => setCompletado(e.target.checked)}
            />
            <label htmlFor="chkCompCav">Marcar como completado</label>
          </div>

          <div className="flex justify-center mt-3">
            <Button
              type="submit"
              className="mx-auto bg-degluapp-green hover:bg-degluapp-green-hover text-white"
              disabled={guardando}
            >
              {guardando ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </form>
      )}

      {/* ====== MECV-V (unificada) ====== */}
      {tipo === "MECV-V" && (
        <div className="bg-[#dfeaf7] p-4 rounded-lg shadow flex flex-col gap-6">
          {/* Intro */}
          <div className="bg-white border rounded-lg p-4 leading-relaxed text-justify">
            <p className="mb-2">
              El MECV-V adaptado es un método clínico que consiste en administrar una serie de bolos de
              3 viscosidades (néctar, líquido y pudín) de 5 a 20 mL de volumen. Estos se suministran
              secuencialmente donde hay una progresión en cuanto a dificultad. En ocasiones, los pacientes
              no toleran 20 mL para la realización de la prueba y se disminuye ese volumen a 15 mL.
            </p>
            <p className="mb-2">
              Debe ser realizado por personal sanitario experimentado / entrenado que sepa interpretarlo.
            </p>
            <p className="font-semibold mb-2">
              Es imprescindible la preparación y colaboración por parte del paciente y, para ello, es necesario:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Explicar al paciente en qué consiste la prueba.</li>
              <li>Se precisa un mínimo estado de alerta que permita colaborar en la prueba.</li>
              <li>Posición adecuada: incorporado, sentado.</li>
              <li>Colocar pulsioxímetro.</li>
              <li>Se le pide que diga su nombre para valorar timbre de voz.</li>
            </ul>
          </div>

          {/* Material + PDF */}
          <div className="bg-white border rounded-lg p-4">
            <h5 className="font-bold mb-3 text-center">MATERIAL NECESARIO</h5>
            <iframe
              src="/docs/mecv-v.pdf"
              title="MECV-V"
              className="w-full h-[480px] border rounded-lg"
            />
            <p className="mt-3 text-center font-semibold text-gray-600">
              RECUERDA: es necesario tener un aspirador para secreciones por seguridad.
            </p>
          </div>

          {/* Iniciar */}
          <div className="flex justify-center">
            <Button
              onClick={() => setItems((prev) => ({ ...prev, iniciar: true }))}
              className="bg-degluapp-green hover:bg-degluapp-green-hover text-white"
            >
              INICIAR MECV-V
            </Button>
          </div>

          {/* Cuadrícula unificada */}
          {items.iniciar && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                guardar();
              }}
              className="flex flex-col gap-6"
            >
              {(() => {
                const VISCOSIDADES = ["nectar", "liquido", "pudin"];
                const VISCO_LABEL = { nectar: "VISCOSIDAD NÉCTAR", liquido: "VISCOSIDAD LÍQUIDA", pudin: "VISCOSIDAD PUDÍN" };
                const VOLUMENES = ["5", "10", "20"]; // ml
                const SEGURIDAD = ["Tos", "Cambio de voz", "Desaturación de oxígeno"];
                const EFICACIA = ["Sello labial", "Residuo oral", "Deglución fraccionada", "Residuo faríngeo"];

                const setCheck = (key, val) =>
                  setItems((prev) => ({
                    ...prev,
                    mecvv: { ...(prev.mecvv || {}), [key]: val },
                  }));

                const Cell = ({ k }) => (
                  <td className="border p-2 text-center align-middle">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={Boolean(items.mecvv?.[k])}
                      onChange={(e) => setCheck(k, e.target.checked)}
                    />
                  </td>
                );

                const Header = () => (
                  <tr>
                    <th className="border p-2 text-left bg-slate-50" />
                    {VISCOSIDADES.map((v) => (
                      <th key={v} className="border p-2 text-center bg-slate-50 font-bold" colSpan={3}>
                        {VISCO_LABEL[v]}
                      </th>
                    ))}
                  </tr>
                );

                const SubHeaderVols = () => (
                  <tr>
                    <th className="border p-2 text-left uppercase tracking-wide text-gray-500"> </th>
                    {VISCOSIDADES.flatMap((v) =>
                      VOLUMENES.map((ml) => (
                        <th key={`${v}_${ml}`} className="border p-2 text-center text-gray-600">
                          {ml} ml
                        </th>
                      ))
                    )}
                  </tr>
                );

                const Block = ({ title, rows, prefix }) => (
                  <div className="bg-white border rounded-lg overflow-x-auto">
                    <div className="px-4 py-3 font-bold bg-gray-600 text-white text-center">{title}</div>
                    <table className="w-full border-collapse">
                      <thead>
                        <Header />
                        <SubHeaderVols />
                      </thead>
                      <tbody>
                        {rows.map((rowLabel) => (
                          <tr key={rowLabel}>
                            <td className="border p-2 font-medium bg-slate-50">{rowLabel}</td>
                            {VISCOSIDADES.flatMap((v) =>
                              VOLUMENES.map((ml) => {
                                const key = `${prefix}_${rowLabel}_${v}_${ml}`;
                                return <Cell key={key} k={key} />;
                              })
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );

                return (
                  <>
                    <Block title="ALTERACIONES O SIGNOS DE SEGURIDAD" rows={SEGURIDAD} prefix="seg" />
                    <Block title="ALTERACIONES O SIGNOS DE EFICACIA" rows={EFICACIA} prefix="efi" />
                  </>
                );
              })()}

          <div>
            <label className="font-semibold block mb-1">Resultado</label>
            <select
              className="border rounded p-2 bg-white w-full"
              value={resultado}
              onChange={(e)=> setResultado(e.target.value)}
            >
              <option value="">Seleccione…</option>
              <option value="SIN alteraciones de SEGURIDAD, NI de EFICACIA">SIN alteraciones de SEGURIDAD, NI de EFICACIA</option>
              <option value="CON alteraciones de SEGURIDAD, pero NO de EFICACIA">CON alteraciones de SEGURIDAD, pero NO de EFICACIA</option>
              <option value="SIN alteraciones de SEGURIDAD, pero SÍ de EFICACIA">SIN alteraciones de SEGURIDAD, pero SÍ de EFICACIA</option>
              <option value="CON alteraciones de SEGURIDAD, Y de EFICACIA">CON alteraciones de SEGURIDAD, Y de EFICACIA</option>
            </select>
          </div>

          <div className="bg-white border rounded-lg p-4">
            <h5 className="font-bold mb-3 text-center">RECOMENDACIONES DIETÉTICAS</h5>
             <label className="font-semibold block mb-1">VISCOSIDAD</label>
<select
  className="border rounded p-2 bg-white w-full"
  value={items.mecvv?.viscosidad || ""}
  onChange={(e) => setMecvvField("viscosidad", e.target.value)}
>
  <option value="">Seleccione…</option>
  <option value="NÉCTAR">NÉCTAR</option>
  <option value="LÍQUIDA">LÍQUIDA</option>
  <option value="PUDDING">PUDDING</option>
</select>

             <p><br/></p>

             <label className="font-semibold block mb-1">VOLUMEN</label>
<select
  className="border rounded p-2 bg-white w-full"
  value={items.mecvv?.volumen || ""}
  onChange={(e) => setMecvvField("volumen", e.target.value)}
>
  <option value="">Seleccione…</option>
  <option value="BAJO 5ml">BAJO 5ml</option>
  <option value="MEDIO 10ml">MEDIO 10ml</option>
  <option value="ALTO 20ml">ALTO 20ml</option>
</select>
          </div>

              {/* Completado y guardar */}
              <div className="flex items-center gap-2">
                <input
                  id="chkCompMecvv"
                  type="checkbox"
                  className="w-4 h-4"
                  checked={completado}
                  onChange={(e) => setCompletado(e.target.checked)}
                />
                <label htmlFor="chkCompMecvv">Marcar como completado</label>
              </div>

              <div className="flex justify-center">
                <Button
                  type="submit"
                  className="mx-auto bg-degluapp-green hover:bg-degluapp-green-hover text-white"
                  disabled={guardando}
                >
                  {guardando ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ====== PLANTILLA BÁSICA PARA OTROS ====== */}
      {tipo === "BASIC" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            guardar();
          }}
          className="bg-[#dfeaf7] p-4 rounded-lg shadow flex flex-col gap-4"
        >
          <div>
            <label className="font-semibold">Notas (opcional)</label>
            <textarea
              className="w-full border rounded p-2 min-h-[96px] bg-white"
              value={items.notas ?? ""}
              onChange={(e) => onChangeBasico("notas", e.target.value)}
              placeholder="Observaciones, hallazgos, etc."
            />
          </div>

          <div>
            <label className="font-semibold">Puntuación total (opcional)</label>
            <input
              type="number"
              className="w-full border rounded p-2 bg-white"
              value={Number.isFinite(puntuacion) ? puntuacion : ""}
              onChange={(e) => onChangeBasico("puntuacionTotal", e.target.value)}
              placeholder="Ej. 12"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="chkCompBasic"
              type="checkbox"
              className="w-4 h-4"
              checked={completado}
              onChange={(e) => setCompletado(e.target.checked)}
            />
            <label htmlFor="chkCompBasic">
              Marcar como completado
            </label>
          </div>

          <div className="flex justify-center">
            <Button
              type="submit"
              className="mx-auto bg-degluapp-green hover:bg-degluapp-green-hover text-white"
              disabled={guardando}
            >
              {guardando ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}